import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, LogOut, Images, FolderKanban, Trash2, Loader2, Plus, Pencil, X } from "lucide-react";
import { resolveMedia } from "../data/projects";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  { id: "roads", label: "طرق وجسور" },
  { id: "water", label: "بنى تحتية" },
  { id: "buildings", label: "أبنية وتطوير" },
];

const inputCls =
  "w-full rounded-md border border-[#E8DFC5] bg-[#FBF6E9] px-4 py-3 text-base text-stone-800 placeholder:text-stone-400 outline-none transition-colors duration-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40";

const formatErr = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return fallback;
};

const ICON_OPTIONS = [
  { id: "landmark", label: "صرح حكومي" },
  { id: "building2", label: "برج" },
  { id: "building", label: "بناء" },
  { id: "factory", label: "مصنع" },
  { id: "hardhat", label: "خوذة عمل" },
  { id: "mountain", label: "جبل" },
];

const resolvePartnerIcon = (txt) => {
  const t = (txt || "").trim();
  if (!t) return "building2";
  if (ICON_OPTIONS.some((o) => o.id === t)) return t;
  if (/صرح|حكوم|وزار|أمانة|امانة|دائرة/.test(t)) return "landmark";
  if (/برج|أبراج|ابراج|عقار|سكن/.test(t)) return "building2";
  if (/بناء|بناية|مبنى|إنشاء|انشاء|عمران/.test(t)) return "building";
  if (/مصنع|صناع|إنتاج|انتاج|توريد/.test(t)) return "factory";
  if (/خوذ|مقاول|تنفيذ|عمل|عمال/.test(t)) return "hardhat";
  if (/جبل|محجر|حجر|ركام|كسار/.test(t)) return "mountain";
  return "building2";
};

const partnerIconLabel = (id) => ICON_OPTIONS.find((o) => o.id === id)?.label || id;

function PartnersTab({ auth }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const load = () => axios.get(`${API}/partners`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const f = e.target.elements;
    setBusy(true);
    try {
      let logo = editing?.logo || "";
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        const up = await axios.post(`${API}/admin/upload`, fd, auth);
        logo = up.data.path;
      }
      const payload = { name: f.pname.value, tag: f.ptag.value, icon: resolvePartnerIcon(f.picon.value), logo };
      if (editing) {
        await axios.put(`${API}/admin/partners/${editing.id}`, payload, auth);
        toast.success("تم حفظ التعديلات — الموقع محدّث الآن");
      } else {
        await axios.post(`${API}/admin/partners`, payload, auth);
        toast.success("تمت إضافة الشريك — يظهر الآن في الموقع");
      }
      setEditing(null);
      setLogoFile(null);
      e.target.reset();
      load();
    } catch (err) {
      toast.error(formatErr(err, "فشل حفظ الشريك"));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("حذف هذا الشريك من الموقع؟")) return;
    try {
      await axios.delete(`${API}/admin/partners/${id}`, auth);
      toast.success("تم حذف الشريك");
      if (editing?.id === id) setEditing(null);
      load();
    } catch (err) {
      toast.error(formatErr(err, "فشل الحذف"));
    }
  };

  return (
    <div>
      <form
        data-testid="partner-add-form"
        key={editing ? editing.id : "new"}
        onSubmit={submit}
        className={`rounded-lg border bg-white p-6 lg:p-8 mb-10 transition-colors ${editing ? "border-amber-500/50" : "border-[#E8DFC5]"}`}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-lg text-stone-900">
            {editing ? `تعديل: ${editing.name}` : "إضافة شريك جديد"}
          </h3>
          {editing && (
            <button
              data-testid="partner-cancel-edit"
              type="button"
              onClick={() => { setEditing(null); setLogoFile(null); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E8DFC5] px-4 py-1.5 text-xs text-stone-600 hover:border-red-500/50 hover:text-red-400 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              إلغاء التعديل
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <input data-testid="partner-name-input" name="pname" required defaultValue={editing?.name || ""} placeholder="اسم الشريك (مثال: شركة فرهنك)" className={inputCls} />
          <input data-testid="partner-tag-input" name="ptag" defaultValue={editing?.tag || ""} placeholder="الوصف (مثال: كركوك — الدرجة الأولى)" className={inputCls} />
          <input
            data-testid="partner-icon-select"
            name="picon"
            defaultValue={editing ? partnerIconLabel(editing.icon) : ""}
            placeholder="الصنف — اكتب ما تريد (مثال: مقاول، مصنع، وزارة...)"
            className={inputCls}
          />
        </div>
        <div className="mb-6">
          <label className="block text-sm text-stone-600 mb-2">شعار الشركة (لوكو) — اختياري، يظهر بجانب الاسم في الموقع</label>
          <div className="flex items-center gap-4 flex-wrap">
            {(logoFile || editing?.logo) && (
              <img
                data-testid="partner-logo-preview"
                src={logoFile ? URL.createObjectURL(logoFile) : resolveMedia(editing.logo)}
                alt="معاينة الشعار"
                className="w-14 h-14 rounded-full object-contain bg-white border border-[#E8DFC5] p-1.5"
              />
            )}
            <input
              data-testid="partner-logo-input"
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              className="text-sm text-stone-600 file:mr-4 file:rounded-full file:border-0 file:bg-emerald-500/10 file:px-4 file:py-2 file:text-emerald-700 hover:file:bg-emerald-500/20"
            />
          </div>
        </div>
        <button
          data-testid="partner-submit-button"
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 font-bold text-white hover:bg-emerald-400 transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
          {editing ? "حفظ التعديلات" : "إضافة الشريك"}
        </button>
      </form>

      <div className="flex flex-col gap-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`partner-item-${p.id}`} className="flex items-center gap-4 rounded-lg border border-[#E8DFC5] bg-white p-4">
            {p.logo ? (
              <img src={resolveMedia(p.logo)} alt={p.name} className="w-11 h-11 rounded-full object-contain bg-white border border-[#E8DFC5] p-1 shrink-0" />
            ) : (
              <span className="w-11 h-11 rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-emerald-600" strokeWidth={1.8} />
              </span>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-base text-stone-800 truncate">{p.name}</p>
              <p className="text-xs text-stone-500">{p.tag}</p>
            </div>
            <button
              data-testid={`partner-edit-${p.id}`}
              onClick={() => {
                setEditing(p);
                setLogoFile(null);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="shrink-0 w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-400 flex items-center justify-center hover:bg-amber-500 hover:text-white transition-colors"
              aria-label="تعديل"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              data-testid={`partner-delete-${p.id}`}
              onClick={() => del(p.id)}
              className="shrink-0 w-9 h-9 rounded-full bg-red-500/10 border border-red-500/40 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
              aria-label="حذف"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem("fb_admin_token", data.token);
      onLogin(data.token);
    } catch (err) {
      setError(formatErr(err, "تعذر تسجيل الدخول"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen flex items-center justify-center px-5 blueprint-grid">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-md rounded-lg border border-[#E8DFC5] bg-white p-8 lg:p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="w-11 h-11 rounded-sm bg-emerald-500 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" strokeWidth={2.4} />
          </span>
          <div>
            <h1 className="font-heading font-extrabold text-xl text-stone-900">لوحة التحكم</h1>
            <p className="text-xs text-stone-500">فكرة بغداد — دخول المدير</p>
          </div>
        </div>
        <label className="block text-sm text-stone-500 mb-2">اسم المستخدم</label>
        <input
          data-testid="admin-login-username-input"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          dir="ltr"
          className={`${inputCls} text-left mb-5`}
          placeholder="Username"
        />
        <label className="block text-sm text-stone-500 mb-2">كلمة المرور</label>
        <input
          data-testid="admin-login-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          dir="ltr"
          className={`${inputCls} text-left mb-5`}
          placeholder="Password"
        />
        {error && (
          <p data-testid="admin-login-error" className="mb-5 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit-button"
          type="submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-8 py-3.5 font-bold text-white transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "تسجيل الدخول"}
        </button>
      </form>
    </div>
  );
}

function GalleryTab({ auth }) {
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/gallery`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const file = e.target.elements.photo.files[0];
    if (!file) return toast.error("اختر صورة أولاً");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/admin/upload`, fd, auth);
      await axios.post(`${API}/admin/gallery`, { caption, src: up.data.path }, auth);
      toast.success("تمت إضافة الصورة إلى المعرض المتحرك");
      setCaption("");
      e.target.reset();
      load();
    } catch (err) {
      toast.error(formatErr(err, "فشل رفع الصورة"));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("حذف هذه الصورة من المعرض؟")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, auth);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(formatErr(err, "فشل الحذف"));
    }
  };

  return (
    <div>
      <form data-testid="gallery-add-form" onSubmit={add} className="rounded-lg border border-[#E8DFC5] bg-white p-6 lg:p-8 mb-10">
        <h3 className="font-heading font-bold text-lg text-stone-900 mb-5">إضافة صورة جديدة للمعرض المتحرك</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <input
            data-testid="gallery-add-caption-input"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            required
            placeholder="وصف الصورة (مثال: أعمال الصب — مشروع كذا)"
            className={inputCls}
          />
          <input
            data-testid="gallery-add-file-input"
            name="photo"
            type="file"
            accept="image/*"
            required
            className={`${inputCls} file:ml-4 file:rounded-full file:border-0 file:bg-emerald-500 file:px-4 file:py-1.5 file:text-sm file:font-bold file:text-white`}
          />
        </div>
        <button
          data-testid="gallery-add-submit"
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 font-bold text-white hover:bg-emerald-400 transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
          إضافة الصورة
        </button>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} data-testid={`gallery-item-${g.id}`} className="group relative rounded-lg overflow-hidden border border-[#E8DFC5] bg-white">
            <img src={resolveMedia(g.src)} alt={g.caption} className="w-full aspect-square object-cover" />
            <div className="p-3 flex items-center justify-between gap-2">
              <p className="text-xs text-stone-600 leading-snug">{g.caption}</p>
              <button
                data-testid={`gallery-delete-${g.id}`}
                onClick={() => del(g.id)}
                className="shrink-0 w-8 h-8 rounded-full bg-red-500/10 border border-red-500/40 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
                aria-label="حذف"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectsTab({ auth }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => axios.get(`${API}/projects`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const f = e.target.elements;
    const mainImage = f.image.files[0];
    if (!editing && !mainImage) return toast.error("اختر الصورة الرئيسية للمشروع");
    setBusy(true);
    try {
      const upload = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const up = await axios.post(`${API}/admin/upload`, fd, auth);
        return up.data.path;
      };
      const imagePath = mainImage ? await upload(mainImage) : editing.image;
      const extraFiles = Array.from(f.gallery.files || []);
      const gallery = editing ? [...(editing.gallery || [])] : [];
      if (!editing) gallery.push({ src: imagePath, caption: f.title.value });
      for (const file of extraFiles) {
        gallery.push({ src: await upload(file), caption: f.title.value });
      }
      const cat = CATEGORIES.find((c) => c.id === f.category.value);
      const payload = {
        title: f.title.value,
        category: cat.id,
        categoryLabel: cat.label,
        location: f.location.value,
        client: f.client.value,
        excerpt: f.excerpt.value,
        story: f.story.value.split("\n").map((s) => s.trim()).filter(Boolean),
        highlights: f.highlights.value.split("\n").map((s) => s.trim()).filter(Boolean),
        image: imagePath,
        gallery,
      };
      if (f.en_title.value.trim()) {
        payload.en = {
          title: f.en_title.value.trim(),
          categoryLabel: { roads: "Roads & Bridges", water: "Infrastructure", buildings: "Buildings & Development" }[cat.id],
          location: f.en_location.value,
          client: f.en_client.value,
          excerpt: f.en_excerpt.value,
          story: f.en_story.value.split("\n").map((s) => s.trim()).filter(Boolean),
          highlights: f.en_highlights.value.split("\n").map((s) => s.trim()).filter(Boolean),
        };
      }
      if (editing) {
        await axios.put(`${API}/admin/projects/${editing.id}`, payload, auth);
        toast.success("تم حفظ التعديلات — الموقع محدّث الآن");
      } else {
        await axios.post(`${API}/admin/projects`, payload, auth);
        toast.success("تمت إضافة المشروع — يظهر الآن في الموقع");
      }
      setEditing(null);
      e.target.reset();
      load();
    } catch (err) {
      toast.error(formatErr(err, "فشل حفظ المشروع"));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("حذف هذا المشروع من الموقع؟")) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`, auth);
      toast.success("تم حذف المشروع");
      if (editing?.id === id) setEditing(null);
      load();
    } catch (err) {
      toast.error(formatErr(err, "فشل الحذف"));
    }
  };

  return (
    <div>
      <form
        data-testid="project-add-form"
        key={editing ? editing.id : "new"}
        onSubmit={submit}
        className={`rounded-lg border bg-white p-6 lg:p-8 mb-10 transition-colors ${editing ? "border-amber-500/50" : "border-[#E8DFC5]"}`}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-lg text-stone-900">
            {editing ? `تعديل: ${editing.title}` : "إضافة مشروع جديد"}
          </h3>
          {editing && (
            <button
              data-testid="project-cancel-edit"
              type="button"
              onClick={() => setEditing(null)}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E8DFC5] px-4 py-1.5 text-xs text-stone-600 hover:border-red-500/50 hover:text-red-400 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              إلغاء التعديل
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <input data-testid="project-title-input" name="title" required defaultValue={editing?.title || ""} placeholder="اسم المشروع" className={inputCls} />
          <select data-testid="project-category-select" name="category" defaultValue={editing?.category || "roads"} className={inputCls}>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <input data-testid="project-location-input" name="location" defaultValue={editing?.location || ""} placeholder="الموقع (مثال: بغداد — الكرخ)" className={inputCls} />
          <input data-testid="project-client-input" name="client" defaultValue={editing?.client || ""} placeholder="العميل / الشريك (مثال: أمانة بغداد)" className={inputCls} />
        </div>
        <textarea data-testid="project-excerpt-input" name="excerpt" rows={2} defaultValue={editing?.excerpt || ""} placeholder="نبذة قصيرة تظهر في بطاقة المشروع" className={`${inputCls} resize-none mb-4`} />
        <textarea data-testid="project-story-input" name="story" rows={5} defaultValue={editing ? (editing.story || []).join("\n") : ""} placeholder={"قصة التنفيذ الكاملة — كل فقرة في سطر مستقل"} className={`${inputCls} resize-none mb-4`} />
        <textarea data-testid="project-highlights-input" name="highlights" rows={3} defaultValue={editing ? (editing.highlights || []).join("\n") : ""} placeholder={"أبرز الملامح — كل ميزة في سطر مستقل"} className={`${inputCls} resize-none mb-4`} />
        <div className="rounded-md border border-dashed border-[#E8DFC5] p-4 mb-4">
          <p className="text-sm font-semibold text-stone-600 mb-3">
            الترجمة الإنجليزية (اختياري — تظهر عند تبديل الزائر للغة الإنجليزية)
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <input data-testid="project-en-title-input" name="en_title" defaultValue={editing?.en?.title || ""} placeholder="Project title (English)" dir="ltr" className={`${inputCls} text-left`} />
            <input data-testid="project-en-location-input" name="en_location" defaultValue={editing?.en?.location || ""} placeholder="Location (English)" dir="ltr" className={`${inputCls} text-left`} />
            <input data-testid="project-en-client-input" name="en_client" defaultValue={editing?.en?.client || ""} placeholder="Client / Partner (English)" dir="ltr" className={`${inputCls} text-left`} />
          </div>
          <textarea data-testid="project-en-excerpt-input" name="en_excerpt" rows={2} defaultValue={editing?.en?.excerpt || ""} placeholder="Short excerpt shown on the card (English)" dir="ltr" className={`${inputCls} text-left resize-none mb-4`} />
          <textarea data-testid="project-en-story-input" name="en_story" rows={4} defaultValue={editing ? (editing.en?.story || []).join("\n") : ""} placeholder={"Full execution story (English) — one paragraph per line"} dir="ltr" className={`${inputCls} text-left resize-none mb-4`} />
          <textarea data-testid="project-en-highlights-input" name="en_highlights" rows={3} defaultValue={editing ? (editing.en?.highlights || []).join("\n") : ""} placeholder={"Project highlights (English) — one per line"} dir="ltr" className={`${inputCls} text-left resize-none`} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm text-stone-500 mb-2">
              {editing ? "الصورة الرئيسية (اتركها فارغة للإبقاء على الحالية)" : "الصورة الرئيسية (إلزامية)"}
            </label>
            <input data-testid="project-image-input" name="image" type="file" accept="image/*" required={!editing} className={`${inputCls} file:ml-4 file:rounded-full file:border-0 file:bg-emerald-500 file:px-4 file:py-1.5 file:text-sm file:font-bold file:text-white`} />
          </div>
          <div>
            <label className="block text-sm text-stone-500 mb-2">
              {editing ? "إضافة صور جديدة للمعرض (تُضاف للحالية)" : "صور إضافية للمعرض (اختياري)"}
            </label>
            <input data-testid="project-gallery-input" name="gallery" type="file" accept="image/*" multiple className={`${inputCls} file:ml-4 file:rounded-full file:border-0 file:bg-emerald-500 file:px-4 file:py-1.5 file:text-sm file:font-bold file:text-white`} />
          </div>
        </div>
        <button
          data-testid="project-submit-button"
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 font-bold text-white hover:bg-emerald-400 transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
          {busy ? "جارٍ رفع الصور والحفظ..." : editing ? "حفظ التعديلات" : "إضافة المشروع"}
        </button>
      </form>

      <div className="flex flex-col gap-3">
        {items.map((p) => (
          <div key={p.id} data-testid={`project-item-${p.id}`} className="flex items-center gap-4 rounded-lg border border-[#E8DFC5] bg-white p-4">
            <img src={resolveMedia(p.image)} alt={p.title} className="w-16 h-16 rounded-md object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-base text-stone-800 truncate">{p.title}</p>
              <p className="text-xs text-stone-500">{p.categoryLabel} — {p.location}</p>
            </div>
            <button
              data-testid={`project-edit-${p.id}`}
              onClick={() => {
                setEditing(p);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="shrink-0 w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-400 flex items-center justify-center hover:bg-amber-500 hover:text-white transition-colors"
              aria-label="تعديل"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              data-testid={`project-delete-${p.id}`}
              onClick={() => del(p.id)}
              className="shrink-0 w-9 h-9 rounded-full bg-red-500/10 border border-red-500/40 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"
              aria-label="حذف"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("fb_admin_token"));
  const [tab, setTab] = useState("gallery");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem("fb_admin_token");
        setToken(null);
      });
  }, [token]);

  if (!token) return <LoginScreen onLogin={setToken} />;

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  return (
    <div dir="rtl" data-testid="admin-dashboard" className="min-h-screen">
      <header className="sticky top-0 z-40 bg-[#FBF6E9]/85 backdrop-blur-xl border-b border-[#E8DFC5]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-sm bg-emerald-500 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" strokeWidth={2.4} />
            </span>
            <span className="font-heading font-bold text-base text-stone-900">لوحة تحكم فكرة بغداد</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="text-sm text-stone-500 hover:text-emerald-600 transition-colors px-3 py-2">
              عرض الموقع
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={() => { localStorage.removeItem("fb_admin_token"); setToken(null); }}
              className="inline-flex items-center gap-2 rounded-full border border-[#E8DFC5] px-4 py-2 text-sm text-stone-600 hover:border-red-500/50 hover:text-red-400 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              خروج
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex gap-3 mb-10">
          <button
            data-testid="admin-tab-gallery"
            onClick={() => setTab("gallery")}
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all ${
              tab === "gallery" ? "bg-emerald-500 text-white" : "border border-[#E8DFC5] text-stone-600 hover:border-emerald-500/50"
            }`}
          >
            <Images className="w-4 h-4" />
            المعرض المتحرك
          </button>
          <button
            data-testid="admin-tab-projects"
            onClick={() => setTab("projects")}
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all ${
              tab === "projects" ? "bg-emerald-500 text-white" : "border border-[#E8DFC5] text-stone-600 hover:border-emerald-500/50"
            }`}
          >
            <FolderKanban className="w-4 h-4" />
            المشاريع
          </button>
          <button
            data-testid="admin-tab-partners"
            onClick={() => setTab("partners")}
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-all ${
              tab === "partners" ? "bg-emerald-500 text-white" : "border border-[#E8DFC5] text-stone-600 hover:border-emerald-500/50"
            }`}
          >
            <Building2 className="w-4 h-4" />
            الشركاء
          </button>
        </div>

        {tab === "gallery" && <GalleryTab auth={auth} />}
        {tab === "projects" && <ProjectsTab auth={auth} />}
        {tab === "partners" && <PartnersTab auth={auth} />}
      </div>
    </div>
  );
}
